<template lang="pug">
.container(
  :data-cnstrc-item-id="props.buyRequest.productId"
  data-cnstrc-item-name="Monster Mass Storage"
  :data-cnstrc-item-price="groupMeta.price ?? groupMeta.total ?? undefined"
)
  .unit-badges
    ProductBadges(v-if="badgeMeta" :meta="badgeMeta" :parentMeta="groupMeta" theme="group-child")
  Accordion.accordian-styles(
    v-if="storageUnits"
    v-for="(unit, i) in storageUnits"
    :modelValue="openAccordionIndex === i"
    :key="unit.id"
    :heading="unitName(i)"
    open-icon="arrow-up"
    closed-icon="arrow-down"
    :theme="'custom-rhpa-styles'"
  )
    .delete-unit(v-if="i !== 0" @click="displayModal(unit.id)") {{ __("Delete Unit") }}
    .side-selection-container
      Modal(v-if="unitPendingDeletionId === unit.id"  @close="unitPendingDeletionId = null")
        h2.center {{ __("Are you sure?")}}
        div.center.delete-info  {{ __("This action can't be undone.")}}
        RogueButton.delete-btn(width="full" color="black" @click="removeUnit(unit.id)")
          div {{ __('Delete Unit') }}
    slot(v-if="validBundleUnitBRs" name="rhpaBody")
      rhpa-body(:buyRequest="unit.child" :groupMeta="groupMeta")
  .button-container
    template(v-if='storageUnits?.length === 1')
      .left-wrapper(@mouseover="isLeftHovered = true" @mouseleave="isLeftHovered = false" )
        RogueButton(width="full" color="black-outline" @click="() => addUnit('left')")
            .btn-content
              Icon.storage-icon.left-icon(
                name="rackbuilder/storage_left"
                :fill="isLeftHovered ? 'white' : 'black'"
              )
              div.add-text {{ __('ADD LEFT UNIT') }}
      .right-wrapper(@mouseover="isRightHovered = true" @mouseleave="isRightHovered = false" )
        RogueButton(width="full" color="black-outline" @click="() => addUnit('right')")
            .btn-content
              Icon.storage-icon.right-icon(
                name="rackbuilder/storage_right"
                :fill="isRightHovered ? 'white' : 'black'"
              )
              div.add-text {{ __('ADD RIGHT UNIT') }}
    template(v-else)
      .button-wrapper(@mouseover="isSingleUnitHovered = true" @mouseleave="isSingleUnitHovered = false" )
        RogueButton(width="full" color="black-outline" @click="addDefaultSideUnit")
          .btn-content
            Icon.storage-icon(:name="defaultSide === 'left' ? 'rackbuilder/storage_left' : 'rackbuilder/storage_right'" :fill="isSingleUnitHovered ? 'white' : 'black'")
            div.add-text {{ __('ADD UNIT') }}
  Accordion.accordian-styles.mass-storage-wheels(v-if="!isAnyUnit100Height && storageUnits?.length <= 3" :heading="__(`Add Storage Wheels`)" open-icon="arrow-up" closed-icon="arrow-down")
    Swatches.wheels(
      label="Add Wheels?"
      :required="true"
      :options="[{label: 'No', value: 'no'}, {label:'Yes', value: 'yes'}]"
      :modelValue="footSelection"
      @update:modelValue="handleFootInput"
    )
  .divider
</template>
<script lang="ts" setup>
import { ref, computed, watch, nextTick, onMounted, onServerPrefetch, inject, provide, toRef } from 'vue'
import RhpaBody from './rhpa-body.vue'
import ProductBadges from './badges.vue'
import { useUnitAccordions } from './custom/useUnitAccordions'
import { getBuyRequestMeta, getSimpleForBuyRequest } from '@/js/helpers/buy-request'
import { useRogue } from '@/js/services/Rogue'
import { useProductStore } from '@/js/stores/productStore'
import type { BuyRequest } from '@/js/services/RogueTypes'
import { findBundleProduct } from '@/js/pages/product/rackbuilder/utils/rackbuilder-util'
import { useMassStorage } from '@/js/composables/useMassStorage'
import { getTallestUnitId, raiseShortKitPanels } from '@/js/pages/product/rackbuilder/utils/mms-utils/mms-panel-utils'

const props = defineProps<{buyRequest: BuyRequest}>()
const updateBuyRequest = inject<(val: BuyRequest) => void>('updateBuyRequest')

const tallestBaseId = ref<number | undefined>(undefined)
provide('tallestBaseId', tallestBaseId)

const rogue = useRogue()
const productStore = useProductStore()
const buyRequestRef = toRef(props, 'buyRequest')
const { STORAGE_SKUS } = useMassStorage(buyRequestRef)

const groupMeta = computed(() => getBuyRequestMeta(props.buyRequest, rogue))

function selectDefaultSide(): 'right' | 'left' {
  return props.buyRequest.children
    ?.map(item => item.metadata?.side as 'right' | 'left')
    .filter(side => ['right', 'left'].includes(side))[0] || 'left'
}

// Must be computed so SKU selection (MMS vs MS) re-evaluates after the product loads into the store
const storageItems = computed(() => [
  STORAGE_SKUS.value.BASE_UNIT_SKU,
  STORAGE_SKUS.value.ADDON_SKU,
  STORAGE_SKUS.value.FOUR_PACK_WHEEL_SKU,
  STORAGE_SKUS.value.TWO_PACK_WHEEL_SKU,
  ...STORAGE_SKUS.value.BOLT_KIT_SKUS,
])
const isLeftHovered = ref(false)
const isRightHovered = ref(false)
const isSingleUnitHovered = ref(false)
const footSelection = ref<'yes' | 'no'>('no')
const defaultSide = ref<'left' | 'right'>(selectDefaultSide())

function getChildBuyRequestBySku(sku: string) {
  return props.buyRequest.children?.[0]?.children?.find(
    br => productStore.products[br.productId]?.sku === sku
  )
}

const {
  units: storageUnits,
  validBundleUnitBRs,
  openAccordionIndex,
  unitPendingDeletionId,
  addUnit,
  displayModal,
  removeUnit,
} = useUnitAccordions(buyRequestRef, {
  isUnit       : child => productStore.products[child.productId]?.sku === STORAGE_SKUS.value.BASE_UNIT_SKU || !!child.metadata?.side,
  decorateUnit : (child, accordionIndex) => {
    const height = getSimpleForBuyRequest(child, rogue)?.attributes?.mass_height?.toString().replace('"', '')

    child.metadata!.height = height
    child.metadata!.onVirtualMeshClick = (index: number) => {
      accordionIndex.value = index
    }
  },
  onBeforeAdd: (_newUnit, currentUnitCount, side) => {
    // Set defaultSide only on first manual add
    if (currentUnitCount === 1 && side) {
      defaultSide.value = side
    }
  },
  onAfterRemove: (updatedBuyRequest) => {
    isLeftHovered.value = false
    isRightHovered.value = false
    isSingleUnitHovered.value = false

    // Force an update to get the new tallestUnitId when removing a unit
    tallestBaseId.value = getTallestUnitId(updatedBuyRequest, rogue)
  },
  onRidChange: () => {
    if (getTwoPackBR()?.qty !== 0 || getFourPackBR()?.qty !== 0) {
      handleFootInput('yes')
    }
  },
})

// The group's meta only carries a lead time every accessory shares, so badges come from the base unit bundle, as on Cave.
const badgeMeta = computed(() => getBuyRequestMeta(storageUnits.value?.[0]?.child ?? props.buyRequest, rogue))

const isAnyUnit100Height = computed(() => storageUnits.value?.some(unit => {
  const addOn = getSimpleForBuyRequest(unit.child, rogue)
  const height = addOn?.attributes?.mass_height?.toString().replace('"', '')

  if (height === '100') {
    footSelection.value = 'no'
  }

  return height === '100'
}))

const wheelQuantities = computed(() => {
  if (footSelection.value === 'no' || isAnyUnit100Height.value) {
    return { fourPack: 0, twoPack: 0 }
  }

  const unitCount = storageUnits.value.length

  if (unitCount === 1) {
    return { fourPack: 1, twoPack: 0 }
  } if (unitCount === 2) {
    return { fourPack: 1, twoPack: 1 }
  } if (unitCount === 3) {
    return { fourPack: 2, twoPack: 0 }
  }

  return { fourPack: 0, twoPack: 0 }
})
const cnstrcProduct = computed(() => productStore.products[props.buyRequest.productId])

// The heading names the panel the unit ships with, which raiseShortKitPanels can make taller than the height the customer picked.
const kitUnitsById = computed(() => {
  const clone: BuyRequest = JSON.parse(JSON.stringify(props.buyRequest))
  raiseShortKitPanels(clone, rogue)

  return new Map((clone.children ?? []).filter(child => child.metadata?.id).map(child => [child.metadata!.id, child]))
})

const calculateRackHeight = computed(() => (index: number) => {
  const units = validBundleUnitBRs.value
  const unit = units[index]
  if (!unit) return ''

  const addOn = getSimpleForBuyRequest(kitUnitsById.value.get(unit.metadata?.id) ?? unit, rogue)
  if (!addOn?.attributes?.mass_height) return ''

  return addOn.attributes.mass_height.toString()
})

const calculateRackWidth = computed(() => (index: number) => {
  const units = validBundleUnitBRs.value
  const unit = units[index]
  if (!unit) return ''

  const addOn = getSimpleForBuyRequest(unit, rogue)
  if (!addOn?.attributes?.mass_width) return ''

  return addOn.attributes.mass_width?.toString()
})

const hasCorner = computed(() => (i: number): boolean => {
  const unit = storageUnits.value?.[i]

  return !!unit && !!findBundleProduct(rogue, unit.child, 'corner', true)
})

const unitName = computed(() => (i: number) => {
  const height = calculateRackHeight.value(i)

  const width = hasCorner.value(i)
    ? '43" Width - Corner'
    : `${calculateRackWidth.value(i)} Width - Straight Rack`

  return `Unit ${i + 1} - ${height} Height - ${width}`
})

function getFourPackBR() {
  return getChildBuyRequestBySku(STORAGE_SKUS.value.FOUR_PACK_WHEEL_SKU)
}

function getTwoPackBR() {
  return getChildBuyRequestBySku(STORAGE_SKUS.value.TWO_PACK_WHEEL_SKU)
}

function handleFootInput(selection: 'yes' | 'no') {
  footSelection.value = selection
}

function updateBuyRequestWithChanges() {
  // Create a deep copy for modification
  const updatedBuyRequest = JSON.parse(JSON.stringify(props.buyRequest))

  // Will be used in configuration.vue to change panels to an addons or a base
  tallestBaseId.value = getTallestUnitId(updatedBuyRequest, rogue)

  updateBuyRequest?.(updatedBuyRequest)
}

function addDefaultSideUnit() {
  addUnit(defaultSide.value)
}

onServerPrefetch(async () => {
  await productStore.loadAll(storageItems.value, rogue)
})

onMounted(async () => {
  await nextTick()

  if (!productStore.products[STORAGE_SKUS.value.ADDON_SKU] || !productStore.products[STORAGE_SKUS.value.BASE_UNIT_SKU]) {
    await productStore.loadAll(storageItems.value, rogue)
  }
})

function handleConfigurationUpdate() {
  updateBuyRequestWithChanges()
}

provide('handleConfigurationUpdate', handleConfigurationUpdate)

// Then use a watcher on the computed property
watch(wheelQuantities, (newQuantities) => {
  const fourPackWheelBuyRequest = getFourPackBR()
  const twoPackWheelBuyRequest = getTwoPackBR()

  if (!fourPackWheelBuyRequest || !twoPackWheelBuyRequest) return

  // Only update if quantities actually changed
  if (fourPackWheelBuyRequest.qty !== newQuantities.fourPack || twoPackWheelBuyRequest.qty !== newQuantities.twoPack) {
    const updatedBuyRequest: BuyRequest = JSON.parse(JSON.stringify(props.buyRequest))

    const updatedFourPack = updatedBuyRequest.children?.[0]?.children?.find(br => productStore.products[br.productId]?.sku === STORAGE_SKUS.value.FOUR_PACK_WHEEL_SKU)
    const updatedTwoPack = updatedBuyRequest.children?.[0]?.children?.find(br => productStore.products[br.productId]?.sku === STORAGE_SKUS.value.TWO_PACK_WHEEL_SKU)
    
    if (updatedFourPack && updatedTwoPack) {
      updatedFourPack.qty = newQuantities.fourPack
      updatedTwoPack.qty = newQuantities.twoPack

      updateBuyRequest?.(updatedBuyRequest)
    }
  }
}, { deep: true })
</script>
<style lang="scss">
.accordion {
  border: none !important;

  .heading {
    border: none !important;
    border-top: $border-width-thin solid $color-gray-light !important;
    padding-left: 0 !important;
  }

  .content {
    border-top: none !important;
    border-bottom: none !important;
  }

  button.link:focus, button.a11y:focus  {
    outline: none;
  }
}

</style>
<style lang="scss" scoped>
.container {
  font-size: 14px;
  margin-bottom: 10px;

  .unit-badges :deep(.badges) {
    padding-bottom: $space-xs;
  }

  .button-container {
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-bottom: 25px;

    .left-wrapper, .right-wrapper, .button-wrapper {
      margin-top: 5px;
      width: 100%;
      height: 100%
    }

    .btn-content {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 14px;
      font-size: 14px;
      font-family: $font-condensed;
      font-weight: bold;
      text-transform: uppercase;
      line-height: 14px;

      div {
        margin-left: 8px;
        font-size: 14px;
      }

      .storage-icon {
        height: 20px;
        width: 20px;
        margin-top: 5px;
      }

      .add-text {
        margin-left: 4px !important;
      }
    }
  }

   .delete-unit {
      margin: 5px 0 10px 0 !important;
    }

  .divider {
    height: 1px;
    background-color: $color-gray;
    margin: 8 0 $size-s;
  }

  .wheels-info {
    height: 36px;
    font-size: 12px;
    line-height: 18px;
    margin-bottom: 10px;
  }

  .accordion.accordian-styles {
    h4 {
      color: rgb(0, 0, 0);
      font-size: 14px;
      font-family: $font-condensed;
      font-weight: bold;
      text-transform: uppercase;
      line-height: 14px;
    }

    .content {
      padding-left: 0;
      padding-top: 0;
    }

    .delete-unit {
      width: 58px;
      height: 12px;
      color: rgb(18, 119, 183);
      font-size: 12px;
      font-family: $font-condensed;
      font-weight: normal;
      text-decoration: underline;
      line-height: 12px;
      cursor: pointer;
      margin-bottom: 5px;
    }
  }

  .delete-info {
    margin: 25px 0;
    font-size: 14px;
    font-family: $font-condensed;
    text-align: center;
    line-height: 21px;
  }
}
</style>
